<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import LabelItem from 'dashboard/components-next/Label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/Label/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const showDropdown = ref(false);
const labelSearchQuery = ref('');

// Store the currently hovered label's ID
// Using JS state management instead of CSS :hover / group hover
// This will solve the flickering issue when hovering over the last label item
const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  const filtered = allLabels.value.filter(({ title }) =>
    availableContactLabels.includes(title)
  );
  console.log('[ContactLabels] savedLabels computed:', {
    contactId: props.contactId,
    availableContactLabels: [...availableContactLabels], // Spread to see actual values
    availableContactLabelsLength: availableContactLabels.length,
    allLabelsCount: allLabels.value.length,
    allLabelsTitles: allLabels.value.map(l => l.title),
    filteredCount: filtered.length,
    filtered,
  });
  return filtered;
});

const generateRandomColor = () => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E2',
    '#F8B739',
    '#52B788',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const labelMenuItems = computed(() => {
  const labels =
    allLabels.value
      ?.map(label => ({
        label: label.title,
        value: label.id,
        thumbnail: { name: label.title, color: label.color },
        isSelected: savedLabels.value.some(
          savedLabel => savedLabel.id === label.id
        ),
        action: 'contactLabel',
        isNewTag: false,
      }))
      .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected)) || [];

  if (labelSearchQuery.value && labelSearchQuery.value.trim()) {
    const searchLower = labelSearchQuery.value.trim().toLowerCase();
    const filteredLabels = labels.filter(label =>
      label.label.toLowerCase().includes(searchLower)
    );

    const matchesExisting = labels.some(
      label => label.label.toLowerCase() === searchLower
    );

    if (!matchesExisting) {
      // Add "Create tag" option at the top
      const createTagOption = {
        label: t('LABEL_MGMT.CREATE_TAG', {
          name: labelSearchQuery.value.trim(),
        }),
        value: 'create-new',
        thumbnail: {
          name: labelSearchQuery.value.trim(),
          color: generateRandomColor(),
        },
        isSelected: false,
        action: 'createLabel',
        isNewTag: true,
        newTagName: labelSearchQuery.value.trim(),
      };
      return [createTagOption, ...filteredLabels];
    }

    return filteredLabels;
  }

  return labels;
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async ({ action, value, newTagName }) => {
  try {
    // Get current label titles
    const currentLabels = savedLabels.value.map(label => label.title);
    console.log('[ContactLabels] handleLabelAction called', {
      action,
      value,
      newTagName,
      currentLabels,
    });

    if (action === 'createLabel') {
      // Create and add new label
      const updatedLabels = [...currentLabels, newTagName];
      console.log(
        '[ContactLabels] Creating new label, updatedLabels:',
        updatedLabels
      );

      await store.dispatch('contactLabels/update', {
        contactId: props.contactId,
        labels: updatedLabels,
      });

      // Refresh all labels to get the newly created one
      await store.dispatch('labels/get');

      // Re-fetch contact labels to update the UI
      await store.dispatch('contactLabels/get', props.contactId);

      labelSearchQuery.value = '';
      showDropdown.value = false;
    } else {
      // Find the label title for the ID (value)
      const selectedLabel = allLabels.value.find(label => label.id === value);
      if (!selectedLabel) return;

      // If label is already selected, remove it (toggle behavior)
      if (currentLabels.includes(selectedLabel.title)) {
        const updatedLabels = currentLabels.filter(
          labelTitle => labelTitle !== selectedLabel.title
        );

        await store.dispatch('contactLabels/update', {
          contactId: props.contactId,
          labels: updatedLabels,
        });
      } else {
        // Add the existing label
        const updatedLabels = [...currentLabels, selectedLabel.title];

        await store.dispatch('contactLabels/update', {
          contactId: props.contactId,
          labels: updatedLabels,
        });
      }

      // Re-fetch contact labels to update the UI
      await store.dispatch('contactLabels/get', props.contactId);

      showDropdown.value = false;

      // Show success message
      console.log('[ContactLabels] Label created and assigned successfully');
    }
  } catch (error) {
    console.error('[ContactLabels] Error in handleLabelAction:', error);
    console.error('[ContactLabels] Error details:', {
      message: error.message,
      response: error.response?.data,
      stack: error.stack,
    });
  }
};

const handleRemoveLabel = label => {
  // Note: LabelItem now emits the full label object, not just the ID
  // Original code expected labelId directly, so we pass label.id as value
  console.log('[ContactLabels] Remove label clicked:', label.title);
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);
onMounted(() => {
  if (route.params.contactId) {
    fetchLabels(route.params.contactId);
  }
});

const handleMouseLeave = () => {
  // Reset hover state when mouse leaves the container
  // This ensures all labels return to their default state
  hoveredLabel.value = null;
};

const handleLabelHover = labelId => {
  hoveredLabel.value = labelId;
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-2" @mouseleave="handleMouseLeave">
    <LabelItem
      v-for="label in savedLabels"
      :key="label.id"
      :label="label"
      :is-hovered="hoveredLabel === label.id"
      @remove="handleRemoveLabel"
      @hover="handleLabelHover(label.id)"
    />
    <AddLabel
      :label-menu-items="labelMenuItems"
      @update-label="handleLabelAction"
      @search="labelSearchQuery = $event"
    />
  </div>
</template>
